import React, { useState } from 'react';
import { formatPrice, formatCurrencyReadable, parseReadableNumberToFloat } from '../utils/formatters';
import TokenPriceChanges from './TokenPriceChanges';
import PriceUpdateInfo from './PriceUpdateInfo';
import { formatRelativeTime } from '../utils/dateUtils';
import TokenAssignmentModal from './TokenAssignmentModal';
import TagModal from './tags/TagModal';
import TagButton from './tags/TagButton';
import DelistedIcon from './DelistedIcon';
import RiskScoreBlock from './RiskScoreBlock';
import { isEphemeralTokenId } from '../utils/tokenId';
import Tooltip from './common/Tooltip';

const metricFillColor = (score) => {
  if (score < 25) return '#ef4444';
  if (score < 50) return '#f97316';
  if (score < 75) return '#eab308';
  return '#22c55e';
};

const volumeFillColor = (volumePct) => {
  if (volumePct < 1) return '#ef4444';
  if (volumePct < 3) return '#f97316';
  if (volumePct < 7) return '#eab308';
  return '#22c55e';
};

const MarketMetricRow = ({ label, value, detail, score, color, tooltip }) => {
  const pct = Math.min(100, Math.max(0, score || 0));
  const row = (
    <div className="grid grid-cols-[5.5rem_1fr_auto] items-center gap-2 text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span
        className="h-2 min-w-[5rem] overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        role="progressbar"
        aria-label={`${label} ${detail || value || ''}`}
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span
          className="block h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color || metricFillColor(pct) }}
        />
      </span>
      <span className="text-right font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{value}</span>
      {detail && (
        <span className="col-start-2 col-span-2 -mt-1 text-xs text-gray-400 dark:text-gray-500">{detail}</span>
      )}
    </div>
  );

  if (!tooltip) return row;

  return (
    <Tooltip title={tooltip} position="top" trigger="mouseenter focus click" interactive={false} arrow={true} theme="dark" size="small">
      <div className="cursor-help">{row}</div>
    </Tooltip>
  );
};

const sorsaProfileUrlFromTwitterUrl = (twitterUrl) => {
  if (!twitterUrl) return null;

  try {
    const url = new URL(twitterUrl);
    const hostname = url.hostname.replace(/^www\./, '').toLowerCase();
    if (!['x.com', 'twitter.com'].includes(hostname)) return null;

    const handle = url.pathname
      .split('/')
      .filter(Boolean)[0]
      ?.replace(/^@/, '');

    if (!handle || ['i', 'intent', 'share', 'search'].includes(handle.toLowerCase())) return null;
    return `https://app.sorsa.io/profile/${encodeURIComponent(handle)}`;
  } catch (_) {
    const handle = String(twitterUrl).trim().replace(/^@/, '');
    return handle ? `https://app.sorsa.io/profile/${encodeURIComponent(handle)}` : null;
  }
};

const TokenDetails = ({ token, position, refetch }) => {
  const tokenData = token || {};
  const marketData = tokenData.marketData || {};
  const sorsaProfileUrl = sorsaProfileUrlFromTwitterUrl(tokenData.twitterUrl);

  // Dropdown state for websites/repos
  const [websiteDropdownOpen, setWebsiteDropdownOpen] = useState(false);
  const [repoDropdownOpen, setRepoDropdownOpen] = useState(false);

  // Assignment modal state
  const [assignmentModal, setAssignmentModal] = useState({ isOpen: false, type: 'group' });
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const openAssignment = (type) => setAssignmentModal({ isOpen: true, type });
  const closeAssignment = () => setAssignmentModal((m) => ({ ...m, isOpen: false }));

  // Helper to render link lists
  const renderLinkList = (urls, icon, title, dropdownOpen, setDropdownOpen, color, showFullText = false) => {
    if (!urls || urls.length === 0) return null;
    if (urls.length === 1) {
      const labelForTitle = urls[0];
      return (
        <Tooltip title={`${title}: ${labelForTitle}`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
          <a
            href={urls[0]}
            target="_blank"
            rel="noopener noreferrer"
            className={`mr-2 ${color} hover:opacity-80 flex items-center justify-center`}
            title={`${title}: ${labelForTitle}`}
          >
            {icon}
          </a>
        </Tooltip>
      );
    }
    return (
      <Tooltip
        html={(
          <div className="w-56 max-w-[calc(100vw-2rem)] rounded-md border border-gray-200 bg-white py-1 text-sm shadow-xl dark:border-gray-700 dark:bg-gray-800" onKeyDown={(event) => { if (event.key === 'Escape') setDropdownOpen(false); }}>
            {urls.map((url, index) => {
              let domain;
              try {
                domain = new URL(url).hostname.replace('www.', '');
              } catch (_) {
                domain = url;
              }
              return (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block break-all px-4 py-2 text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setDropdownOpen(false)}
                  title={url}
                >
                  {showFullText ? url : domain}
                </a>
              );
            })}
          </div>
        )}
        visible={dropdownOpen}
        trigger="manual"
        interactive={true}
        position="bottom-start"
        appendTo={() => document.body}
        popperOptions={{ strategy: 'fixed' }}
        onClickOutside={() => setDropdownOpen(false)}
        maxWidth="none"
        className="!bg-transparent [&>.tippy-content]:p-0"
      >
        <button
          type="button"
          className={`mr-2 ${color} hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 flex items-center justify-center`}
          title={`${title} (${urls.length} links available, click to view)`}
          aria-label={title}
          aria-expanded={dropdownOpen}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          onKeyDown={(event) => { if (event.key === 'Escape') setDropdownOpen(false); }}
        >
          {icon}
        </button>
      </Tooltip>
    );
  };

  // Icons
  const websiteIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M21.721 12.752a9.711 9.711 0 00-.945-5.003 12.754 12.754 0 01-4.339 2.708 18.991 18.991 0 01-.214 4.772 17.165 17.165 0 005.498-2.477zM14.634 15.55a17.324 17.324 0 00.332-4.647c-.952.227-1.945.347-2.966.347-1.021 0-2.014-.12-2.966-.347a17.515 17.515 0 00.332 4.647 17.385 17.385 0 005.268 0zM9.772 17.119a18.963 18.963 0 004.456 0A17.182 17.182 0 0112 21.724a17.18 17.18 0 01-2.228-4.605zM7.777 15.23a18.87 18.87 0 01-.214-4.774 12.753 12.753 0 01-4.34-2.708 9.711 9.711 0 00-.944 5.004 17.165 17.165 0 005.498 2.477zM21.356 14.752a9.765 9.765 0 01-7.478 6.817 18.64 18.64 0 001.988-4.718 18.627 18.627 0 005.49-2.098zM2.644 14.752c1.682.971 3.53 1.688 5.49 2.099a18.64 18.64 0 001.988 4.718 9.765 9.765 0 01-7.478-6.816zM13.878 2.43a9.755 9.755 0 016.116 3.986 11.267 11.267 0 01-3.746 2.504 18.63 18.63 0 00-2.37-6.49zM12 2.276a17.152 17.152 0 012.805 7.121c-.897.23-1.837.353-2.805.353-.968 0-1.908-.122-2.805-.353A17.151 17.151 0 0112 2.276zM10.122 2.43a18.629 18.629 0 00-2.37 6.49 11.266 11.266 0 01-3.746-2.504 9.754 9.754 0 016.116-3.985z" />
    </svg>
  );

  const repoIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  );

  const formattedVolume24h = formatCurrencyReadable(marketData.volume24h);
  const marketCapRank = Number(marketData.marketCapRank || 0);
  const marketCapRankTooltip = marketData.timestamp
    ? `Market cap rank updated ${formatRelativeTime(marketData.timestamp)}`
    : 'Market cap rank update time unavailable';
  const marketCapValue = marketData.marketCap && marketData.marketCap !== '0' ? marketData.marketCap : '0';
  const fdvValue = marketData.fullyDilutedValuation && marketData.fullyDilutedValuation !== '0'
    ? marketData.fullyDilutedValuation
    : null;
  const marketCapFdvPct = (() => {
    const ratio = parseFloat(marketData.marketCapFdvRatio);
    if (Number.isFinite(ratio) && ratio > 0) return Math.min(100, Math.max(0, ratio * 100));
    const mc = parseReadableNumberToFloat(marketData.marketCap || '0');
    const fdv = parseReadableNumberToFloat(marketData.fullyDilutedValuation || '0');
    if (mc > 0 && fdv > 0) return Math.min(100, Math.max(0, (mc / fdv) * 100));
    return null;
  })();
  const volume24hPctOfCap = (() => {
    const mc = parseReadableNumberToFloat(marketData.marketCap || '0');
    const fdv = parseReadableNumberToFloat(marketData.fullyDilutedValuation || '0');
    const cap = mc > 0 ? mc : (fdv > 0 ? fdv : 0);
    const vol = parseReadableNumberToFloat(marketData.volume24h);
    if (cap > 0 && vol > 0) {
      return ((vol / cap) * 100).toFixed(1);
    }
    return null;
  })();

  const volumePctNumber = volume24hPctOfCap == null ? null : parseFloat(volume24hPctOfCap);
  const volumeScore = volumePctNumber == null || !Number.isFinite(volumePctNumber)
    ? null
    : Math.min(100, Math.max(0, volumePctNumber * 10));

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 max-w-full grow flex-wrap items-center gap-2">
          <h1 className="min-w-0 max-w-full [overflow-wrap:anywhere] text-xl font-bold leading-tight m-0 sm:text-2xl" title={`${tokenData.name || 'Unknown'} (${tokenData.ticker || 'N/A'})`}>
            {tokenData.name || 'Unknown'} ({tokenData.ticker || 'N/A'})
          </h1>
          {marketCapRank > 0 && (
            <Tooltip title={marketCapRankTooltip} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
              <span
                className="inline-flex shrink-0 items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:text-gray-200"
                title={marketCapRankTooltip}
              >
                #{marketCapRank.toLocaleString()}
              </span>
            </Tooltip>
          )}
          {tokenData.isDelisted && (
            <div className="flex items-center justify-center shrink-0 w-8 h-8">
              <DelistedIcon className="w-5 h-5 flex-shrink-0" />
            </div>
          )}
        </div>
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-y-2">
          {tokenData.coingeckoSlug && (
            <Tooltip title={`View ${tokenData.name} on CoinGecko - Price, charts, and market data`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
              <a
                href={`https://www.coingecko.com/en/coins/${tokenData.coingeckoSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mr-2 flex items-center justify-center"
                title={`View ${tokenData.name} on CoinGecko - Price, charts, and market data`}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/coingecko.png`}
                  alt="CoinGecko"
                  className="w-5 h-5"
                />
              </a>
            </Tooltip>
          )}
          {tokenData.twitterUrl && (
            <>
              <Tooltip title={`View ${tokenData.name} Twitter Profile`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                <a
                  href={tokenData.twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mr-2 text-blue-400 hover:text-blue-500 flex items-center justify-center"
                  title={`View ${tokenData.name} Twitter Profile`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </Tooltip>
              {sorsaProfileUrl && (
                <Tooltip title={`View ${tokenData.name} on Sorsa - social profile intelligence`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                  <a
                    href={sorsaProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-2 flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-[11px] font-bold leading-none text-white hover:bg-violet-500"
                    title={`View ${tokenData.name} on Sorsa - social profile intelligence`}
                  >
                    S
                  </a>
                </Tooltip>
              )}
            </>
          )}

          {renderLinkList(
            tokenData.websiteUrls,
            websiteIcon,
            `Visit ${tokenData.name} Website`,
            websiteDropdownOpen,
            setWebsiteDropdownOpen,
            'text-green-500'
          )}
          {renderLinkList(
            tokenData.repositoryUrls,
            repoIcon,
            `View ${tokenData.name} Source Code`,
            repoDropdownOpen,
            setRepoDropdownOpen,
            'text-gray-500 dark:text-gray-400',
            true
          )}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 max-w-full grow items-center gap-2">
          <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 [overflow-wrap:anywhere]">{formatPrice(marketData.currentPrice)}</span>
          <PriceUpdateInfo timestamp={marketData.timestamp} />
        </div>
        <div className="min-w-0 max-w-full">
          <TokenPriceChanges marketData={marketData} />
        </div>
      </div>

      {(tokenData.riskScore != null || marketCapFdvPct != null || marketData.volume24h) && (
        <div className="mb-4 space-y-2 rounded-md bg-gray-50 p-3 dark:bg-gray-700/40">
          {tokenData.riskScore != null && (
            <RiskScoreBlock riskScore={tokenData.riskScore} tokenName={tokenData.name} />
          )}
          {marketCapFdvPct != null && (
            <MarketMetricRow
              label="MC / FDV"
              value={`${marketCapFdvPct.toFixed(0)}%`}
              detail={`${marketCapValue} MC${fdvValue ? ` / ${fdvValue} FDV` : ''}`}
              score={marketCapFdvPct}
              color={metricFillColor(marketCapFdvPct)}
              tooltip="Market cap compared with fully diluted valuation. Low means a large future unlock/dilution gap; high means circulating value is closer to full diluted value."
            />
          )}
          {marketData.volume24h && volumeScore != null && (
            <MarketMetricRow
              label="Volume"
              value={formattedVolume24h}
              detail={`${volume24hPctOfCap}% of ${marketData.marketCap && marketData.marketCap !== '0' ? 'MC' : 'FDV'} in 24h`}
              score={volumeScore}
              color={volumeFillColor(volumePctNumber)}
              tooltip="24h trading volume compared with market cap or FDV. Low percentage can mean weak liquidity; higher percentage means the token trades more actively. Scale fills at 10%+ daily turnover."
            />
          )}
        </div>
      )}

      {!isEphemeralTokenId(tokenData.id) && (
      <div className="mt-2 flex flex-wrap items-start gap-2">
        <button
          type="button"
          className="inline-flex items-center cursor-pointer text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-3 py-1 rounded-md text-gray-700 dark:text-gray-200"
          onClick={() => openAssignment('group')}
        >
          {tokenData.group ? <span>Group: {tokenData.group.name}</span> : <span>Assign to Group</span>}
        </button>
        {position && (
          <div className="min-w-0 max-w-full flex-1 sm:flex-none">
            <TagButton tags={position.tags} onClick={() => setTagModalOpen(true)} />
          </div>
        )}
      </div>
      )}

      {position && (
        <TagModal
          isOpen={tagModalOpen}
          onClose={() => setTagModalOpen(false)}
          positionId={position.id}
          currentTags={position.tags || []}
          onTagsChanged={refetch}
        />
      )}

      {assignmentModal.isOpen && (
        <TokenAssignmentModal
          isOpen={assignmentModal.isOpen}
          onClose={closeAssignment}
          type={assignmentModal.type}
          tokenId={tokenData.id}
          tokenTicker={tokenData.ticker}
          currentItem={tokenData.group}
          onApplied={refetch}
        />
      )}
    </div>
  );
};

export default TokenDetails;


